import * as React from 'react';
import { useCallback, useRef, useState } from 'react';
import useDropdownSelection from './hooks/useDropdownSelection';
import { useClickedOutside } from '@app/react-common/hooks/useClickOutside';
import usePropSync from './hooks/usePropsSync';
import { DropdownOption } from './DropdownOption';
import { useDropdownPosition } from './hooks/useDropdownPosition';

const DEFAULT_LIST_MAX_HEIGHT = 300;
const DEFAULT_LIST_MAX_WIDTH = '100%';

export interface Option {
  label: string;
  value: any;
  disabled?: boolean;
  disabledOptionsOnly?: false;
  customContent?: React.ReactNode;
}

export interface BaseDropdownProps {
  options: Option[];
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  variant?: 'default' | 'ghost' | 'menu' | 'light';
  showValidation?: boolean;
  invalidMsg?: string;
  listMaxHeight?: number;
  width?: number;
  multiSelect?: boolean;
  disabledOptionsOnly?: boolean;
  position?: 'above' | 'below';
  showSelectUnselectOptions?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  deselectAllLabel?: string;
  deselectAllOnly?: boolean;
  formatSelectedOptionsLabel?: (selectedOptions: Option[]) => string;
}

export interface SingleSelectProps extends BaseDropdownProps {
  preSelectedOption?: Option | null;
  onSelect?: (option: Option | null) => void;
  preSelectedOptions?: never;
}

export interface MultiSelectProps extends BaseDropdownProps {
  preSelectedOptions?: Option[];
  onSelect?: (options: Option[]) => void;
  preSelectedOption?: never;
}

export type DropdownProps = SingleSelectProps | MultiSelectProps;

export default function Dropdown(props: DropdownProps) {
  const {
    options,
    label,
    placeholder = 'Select',
    showValidation = false,
    invalidMsg,
    disabled = false,
    disabledOptionsOnly = false,
    variant = 'default',
    listMaxHeight = DEFAULT_LIST_MAX_HEIGHT,
    width,
    multiSelect,
    showSelectUnselectOptions = false,
    searchable = false,
    searchPlaceholder = 'Search',
    deselectAllLabel = 'Deselect All',
    deselectAllOnly = false,
    formatSelectedOptionsLabel,
  } = props;
  const { preSelectedOption, preSelectedOptions, singleSelectHandler, multiSelectHandler } =
    useDropdownSelection(props);

  const [selectedOption, setSelectedOption] = useState<Option | null>(preSelectedOption);
  const [selectedOptions, setSelectedOptions] = useState<Option[]>(preSelectedOptions);
  const [expanded, setExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  const { position: calculatedPosition, isReady } = useDropdownPosition({
    dropdownRef,
    isExpanded: expanded,
    listMaxHeight,
    forcePosition: props.position,
  });

  useClickedOutside(dropdownRef, () => setExpanded(false));

  const visibleOptions = searchable
    ? options?.filter((option) => option.label.toLowerCase().includes(searchTerm.trim().toLowerCase()))
    : options;

  usePropSync({
    condition: !multiSelect,
    propsValue: preSelectedOption,
    stateValue: selectedOption,
    setStateValue: setSelectedOption,
  });

  usePropSync({
    condition: multiSelect,
    propsValue: preSelectedOptions,
    stateValue: selectedOptions,
    setStateValue: setSelectedOptions,
  });

  const handleMultiSelect = useCallback(
    (option: Option | null) => {
      if (!option) return;

      setSelectedOptions((prevOptions: Option[]) => {
        const optionExists = prevOptions.some((item) => item.value === option.value);

        const newOptions = optionExists
          ? prevOptions.filter((item) => item.value !== option.value)
          : [...prevOptions, option];

        multiSelectHandler?.(newOptions);

        return newOptions;
      });
    },
    [multiSelectHandler],
  );

  const handleSelect = useCallback(
    (option: Option | null) => {
      if (!multiSelect) {
        setSelectedOption(option);
        if (singleSelectHandler) {
          singleSelectHandler(option);
        }
        setExpanded(false);
      } else {
        handleMultiSelect(option);
      }
    },
    [multiSelect, singleSelectHandler, handleMultiSelect],
  );

  const handleSelectUnselectAll = useCallback(
    (selectAll: boolean) => {
      if (!multiSelect) return;

      const newOptions = selectAll ? [...options] : [];
      setSelectedOptions(newOptions);
      multiSelectHandler?.(newOptions);
    },
    [multiSelect, options, multiSelectHandler],
  );

  const hasSelectedOptions = selectedOptions.length > 0;

  const handleSelectUnselectClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      handleSelectUnselectAll(!hasSelectedOptions);
    },
    [hasSelectedOptions, handleSelectUnselectAll],
  );

  const isOptionSelected = useCallback(
    (option: Option): boolean => {
      return multiSelect
        ? selectedOptions.some((item) => item.value === option.value)
        : selectedOption?.value === option.value;
    },
    [multiSelect, selectedOptions, selectedOption],
  );

  const getDropdownClasses = () => {
    const baseClassName = 'pdq-react-dropdown';
    const classes = [baseClassName];

    isInvalid && classes.push(`${baseClassName}--invalid`);
    disabled && classes.push(`${baseClassName}--disabled`);
    expanded && classes.push(`${baseClassName}--dd-expanded`);
    variant === 'ghost' && classes.push(`${baseClassName}--ghost`);
    variant === 'light' && classes.push(`${baseClassName}--light`);
    variant === 'menu' && classes.push(`${baseClassName}--menu`);
    return classes.join(' ');
  };

  const renderOptions = () => {
    const menuClassName = `pdq-react-dropdown__menu ${calculatedPosition === 'above' ? 'pdq-react-dropdown__menu--above' : ''}`;

    return (
      <div
        className={menuClassName}
        style={{ maxHeight: `${listMaxHeight}px`, width: width ? `${width}px` : DEFAULT_LIST_MAX_WIDTH }}>
        {searchable && (
          <div className="pdq-react-dropdown__search">
            <input
              className="pdq-react-dropdown__search-input"
              type="text"
              value={searchTerm}
              placeholder={searchPlaceholder}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        )}
        {showSelectUnselectOptions && multiSelect && (hasSelectedOptions || !deselectAllOnly) && (
          <div className="pdq-react-dropdown__select-unselect-options">
            <button className="pdq-react-dropdown__select-unselect-button" onClick={handleSelectUnselectClick}>
              {hasSelectedOptions ? deselectAllLabel : 'Select All'}
            </button>
          </div>
        )}
        <div className="pdq-react-dropdown__options">
          {visibleOptions?.map((option) => (
            <DropdownOption
              key={String(option.value ?? option.label)}
              option={option}
              isSelected={isOptionSelected(option)}
              multiSelect={multiSelect}
              disabled={option.disabled || disabledOptionsOnly}
              onSelect={handleSelect}
            />
          ))}
        </div>
      </div>
    );
  };

  const renderSelectedContent = () => {
    if (variant === 'menu') {
      return placeholder;
    }

    if (!multiSelect) {
      return selectedOption ? selectedOption.label : placeholder;
    } else {
      if (selectedOptions.length === 0) {
        return placeholder;
      } else {
        return formatSelectedOptionsLabel
          ? formatSelectedOptionsLabel(selectedOptions)
          : `${selectedOptions.length} Selected`;
      }
    }
  };

  const isInvalid =
    showValidation && ((!multiSelect && !selectedOption?.value) || (multiSelect && selectedOptions.length === 0));

  const isIntrinsicWidth = variant === 'light';
  const containerClassName = `pdq-react-dropdown-container${isIntrinsicWidth ? ' pdq-react-dropdown-container--light' : ''}`;

  return (
    <div
      className={containerClassName}
      ref={dropdownRef}
      style={isIntrinsicWidth ? undefined : { width: width ? `${width}px` : '100%' }}>
      {label && <label className="pdq-input-label pdq-input-label--form-field react-input-label">{label}</label>}
      <div
        className={getDropdownClasses()}
        onClick={() => {
          if (!disabled) {
            setSearchTerm('');
            setExpanded((prev) => !prev);
          }
        }}>
        {renderSelectedContent()}
        <div className={`pdq-react-dropdown__icon ${expanded ? 'rotate' : ''} ${disabled ? 'disabled' : ''}`}></div>
      </div>

      {expanded && isReady && renderOptions()}

      {isInvalid && invalidMsg && <span className="invalid-msg">*{invalidMsg}</span>}
    </div>
  );
}
